import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Linking, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import Button from '../src/components/Button';
import ContentContainer from '../src/components/ContentContainer';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';

const APP_STORE_URL = 'https://apps.apple.com/app/id6761732065';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.rinsepay.mobile';

function detectStore(): 'ios' | 'android' | 'both' {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent || '';
    if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
    if (/android/i.test(ua)) return 'android';
  }
  return 'both';
}

/** DownloadScreen - Promotes native mobile app with feature highlights and store badges. */
export default function DownloadScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const store = detectStore();
  const showApple = store === 'ios' || store === 'both';
  const showGoogle = store === 'android' || store === 'both';

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <ContentContainer maxWidth={480}>
        <View style={styles.card}>
          <View style={styles.iconContainer}>
            <Ionicons name="phone-portrait-outline" size={64} color={colors.primary} />
          </View>

          <Text style={styles.title}>Get the RinsePay App</Text>
          <Text style={styles.subtitle}>
            The fastest way to pay at the car wash
          </Text>

          <View style={styles.featureList}>
            <View style={styles.featureRow}>
              <Ionicons name="flash-outline" size={20} color={colors.primary} />
              <Text style={styles.featureText}>Tap to start your wash instantly</Text>
            </View>
            <View style={styles.featureRow}>
              <Ionicons name="card-outline" size={20} color={colors.primary} />
              <Text style={styles.featureText}>Apple Pay & Google Pay support</Text>
            </View>
            <View style={styles.featureRow}>
              <Ionicons name="notifications-outline" size={20} color={colors.primary} />
              <Text style={styles.featureText}>Real-time wash status updates</Text>
            </View>
          </View>

          <View style={styles.comingSoon}>
            <Text style={styles.comingSoonText}>Download on</Text>
            <View style={styles.storeRow}>
              {showApple && (
                <TouchableOpacity
                  style={styles.storeBadge}
                  onPress={() => Linking.openURL(APP_STORE_URL)}
                  accessibilityRole="link"
                  accessibilityLabel="Download on the App Store"
                >
                  <Ionicons name="logo-apple" size={24} color={colors.text} />
                  <Text style={styles.storeText}>App Store</Text>
                </TouchableOpacity>
              )}
              {showGoogle && (
                <TouchableOpacity
                  style={styles.storeBadge}
                  onPress={() => Linking.openURL(PLAY_STORE_URL)}
                  accessibilityRole="link"
                  accessibilityLabel="Get it on Google Play"
                >
                  <Ionicons name="logo-google-playstore" size={24} color={colors.text} />
                  <Text style={styles.storeText}>Google Play</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          <Button
            title="Continue in Browser"
            variant="outline"
            onPress={() => router.back()}
            style={styles.browserButton}
          />
        </View>
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  contentContainer: { padding: spacing.lg, flexGrow: 1, justifyContent: 'center' },
  card: {
    backgroundColor: c.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.xl,
    alignItems: 'center',
  },
  iconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: c.primaryBg,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: fontSize.md,
    color: c.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  featureList: { width: '100%', marginBottom: spacing.xl, gap: spacing.md },
  featureRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  featureText: { fontSize: fontSize.md, color: c.text, flex: 1 },
  comingSoon: { alignItems: 'center', marginBottom: spacing.xl },
  comingSoonText: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    fontWeight: fontWeight.medium,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: spacing.md,
  },
  storeRow: { flexDirection: 'row', gap: spacing.lg },
  storeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: c.surfaceSecondary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm,
  },
  storeText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  browserButton: { width: '100%' },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
